import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Coins, Loader2, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

import { GET_ACCOUNTS } from "@/graphql/queries/admin";
import { GRANT_AI_CREDITS, SET_SUBSCRIPTION } from "@/graphql/mutations/admin";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

/**
 * Who has an account, and whose subscription is open.
 *
 * The AI features cost a provider call per use, so this page is the only key to
 * them. It lists grown-ups as well as learners — `accounts` rather than
 * `students` — because a subscription belongs to whoever owns the learner, and a
 * child's row is decided by their parent's dates.
 */

const ROLE_LABEL: Record<string, string> = {
  admin: "Administratorius",
  guardian: "Tėvas ar mokytojas",
  student: "Mokinys",
};

/** Common windows, so the usual case is one click rather than typing a date. */
const PRESETS = [
  { label: "+1 mėn.", days: 30 },
  { label: "+3 mėn.", days: 90 },
  { label: "+1 metai", days: 365 },
];

/** The two credit packs that can be bought, plus the month's own allowance. */
const CREDIT_PRESETS = [100, 500, 2000];

/**
 * Below this nothing expensive runs — a report and an avatar both cost 10 — so
 * an account here is out of credits in every way that matters to whoever is
 * using it, whatever the number says.
 */
const LOW_BALANCE = 10;

type Account = {
  id: string;
  name: string;
  email: string | null;
  role: string;
  subscribed: boolean;
  subscribedFrom: string | null;
  subscribedUntil: string | null;
  subscriptionHolderId: string;
  aiCredits: number;
  monthlyAiCredits: number;
  metered: boolean;
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function UsersPage() {
  const { data, loading, refetch } = useQuery(GET_ACCOUNTS, { fetchPolicy: "cache-and-network" });
  const [setSubscription, { loading: saving }] = useMutation(SET_SUBSCRIPTION);
  const [grantCredits, { loading: granting }] = useMutation(GRANT_AI_CREDITS);
  const [editing, setEditing] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [topping, setTopping] = useState<string | null>(null);
  const [amount, setAmount] = useState("");

  const accounts: Account[] = (data as any)?.accounts ?? [];

  const save = async (id: string, nextFrom: string | null, nextUntil: string | null) => {
    const result = await setSubscription({
      variables: { studentId: id, subscribedFrom: nextFrom || null, subscribedUntil: nextUntil || null },
    });
    const errors = (result.data as any)?.setSubscription?.errors ?? [];
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    toast.success(nextUntil ? "Prenumerata atnaujinta" : "Prenumerata panaikinta");
    setEditing(null);
    refetch();
  };

  const topUp = async (id: string, credits: number) => {
    if (!Number.isFinite(credits) || credits <= 0) {
      toast.error("Įrašyk teigiamą kreditų kiekį");
      return;
    }

    const result = await grantCredits({ variables: { studentId: id, amount: Math.round(credits) } });
    const errors = (result.data as any)?.grantAiCredits?.errors ?? [];
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    // The mutation returns the new balance on the same row, so the number moves
    // on its own — nothing to refetch.
    toast.success(`Pridėta ${Math.round(credits)} kreditų`);
    setTopping(null);
    setAmount("");
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Vartotojai</h1>
        <p className="text-muted-foreground">
          Prenumerata atrakina AI funkcijas: pokalbius apie užduotis, ataskaitas, užduočių pritaikymą ir
          avatarus. Kreditai — kiek jų dar galima panaudoti šį mėnesį. Vaiko prenumerata ir
          kreditai — tėvų.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Paskyros ({accounts.length})
          </CardTitle>
          <CardDescription>
            Datos imtinai. Tuščia pabaigos data reiškia, kad prenumeratos nėra.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-2">
          {accounts.map((account) => {
            const inherited = account.subscriptionHolderId !== account.id;
            const isEditing = editing === account.id;
            const isTopping = topping === account.id;
            const low = account.metered && account.aiCredits < LOW_BALANCE;

            return (
              <div key={account.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{account.name}</span>
                      <Badge variant="outline">{ROLE_LABEL[account.role] ?? account.role}</Badge>
                      {account.subscribed ? (
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
                          <ShieldCheck className="mr-1 h-3 w-3" />
                          Prenumerata
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Nemokama</Badge>
                      )}
                      {/* An admin pays the provider directly and is never
                          charged, so a balance on their row would be a meter
                          nobody spends — the same reading the billing page
                          takes. Said out loud rather than left blank: a row
                          with no number at all reads as a broken page, not as
                          an account that is never billed. */}
                      {account.metered ? (
                        <Badge
                          variant="outline"
                          className={low ? "border-destructive/40 text-destructive" : undefined}
                          title={`Iš ${account.monthlyAiCredits} per mėnesį`}
                        >
                          <Coins className="mr-1 h-3 w-3" />
                          {account.aiCredits} / {account.monthlyAiCredits} kr.
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground" title="Admino paskyra moka tiekėjui tiesiogiai">
                          <Coins className="mr-1 h-3 w-3" />
                          neskaičiuojama
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {account.email ?? "—"}
                      {account.subscribedUntil && (
                        <>
                          {" · iki "}
                          {account.subscribedUntil}
                          {account.subscribedFrom && ` (nuo ${account.subscribedFrom})`}
                        </>
                      )}
                      {/* A child's row is decided elsewhere — both the window
                          and the balance are the parent's — so say so rather
                          than offering controls that would do nothing here. */}
                      {inherited && " · prenumerata ir kreditai iš tėvų paskyros"}
                    </div>
                  </div>

                  {!inherited && !isEditing && !isTopping && (
                    <div className="flex flex-wrap items-center gap-2">
                      {PRESETS.map((preset) => (
                        <Button
                          key={preset.days}
                          size="sm"
                          variant="outline"
                          disabled={saving}
                          onClick={() => save(account.id, account.subscribedFrom ?? today(), plusDays(preset.days))}
                        >
                          {preset.label}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={saving}
                        onClick={() => {
                          setEditing(account.id);
                          setFrom(account.subscribedFrom ?? "");
                          setUntil(account.subscribedUntil ?? "");
                        }}
                      >
                        Datos
                      </Button>
                      {/* Credits are a separate economy from the window: an
                          account can be paid up and out of credits, or the
                          other way round. Its own control, behind its own
                          click, so the common case stays one line of buttons. */}
                      {account.metered && (
                        <Button
                          size="sm"
                          variant={low ? "outline" : "ghost"}
                          className={low ? "border-destructive/40 text-destructive" : undefined}
                          onClick={() => {
                            setTopping(account.id);
                            setAmount("");
                          }}
                        >
                          <Coins className="mr-1 h-3.5 w-3.5" />
                          Kreditai
                        </Button>
                      )}
                      {account.subscribedUntil && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={saving}
                          onClick={() => save(account.id, null, null)}
                        >
                          Panaikinti
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
                    <label className="text-sm">
                      <span className="mb-1 block text-muted-foreground">Nuo</span>
                      <Input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        className="w-40"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block text-muted-foreground">Iki</span>
                      <Input
                        type="date"
                        value={until}
                        onChange={(e) => setUntil(e.target.value)}
                        className="w-40"
                      />
                    </label>
                    <Button size="sm" disabled={saving} onClick={() => save(account.id, from, until)}>
                      Išsaugoti
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Atšaukti
                    </Button>
                  </div>
                )}

                {isTopping && (
                  <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
                    <div className="mr-1 text-sm text-muted-foreground">
                      Dabar {account.aiCredits} kr. · mėnesio norma {account.monthlyAiCredits}
                    </div>
                    {CREDIT_PRESETS.map((preset) => (
                      <Button
                        key={preset}
                        size="sm"
                        variant="outline"
                        disabled={granting}
                        onClick={() => topUp(account.id, preset)}
                      >
                        +{preset}
                      </Button>
                    ))}
                    <Input
                      type="number"
                      min={1}
                      placeholder="Kiek"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") topUp(account.id, Number(amount));
                      }}
                      className="w-28"
                    />
                    <Button
                      size="sm"
                      disabled={granting || amount.trim() === ""}
                      onClick={() => topUp(account.id, Number(amount))}
                    >
                      Pridėti
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setTopping(null)}>
                      Atšaukti
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
